// Package imports:
import React, { useContext, useEffect, useState } from 'react';
//@ts-ignore
import Columns from 'react-columns';
// Component imports:
import CurrenciesSingleBase from '../CurrenciesSingleBase/CurrenciesSingleBase';
import CurrencyIntradayChart, { CurrenciesChartType } from '../IntradayCharts/CurrencyIntradayChart/CurrencyIntradayChart';
import AdRotator from '../Ad/AdRotator';
import Label from '../../ui-elements/Label/Label';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import GjaldmidlarChart from './GjaldmidlarChart';
// Service imports:
import { MARKET_REFRESH_RATES } from '../../services/config';
import { useApiLmdDataMappedByString } from '../../services/apiHooks';
import { getAPItoday } from '../../services/utils';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdCurrency } from '../../types/CurrencyTypes';
// Context imports:
import { AccessTokenContext } from '../../contexts/AccessTokenContext';

interface IVisibleCurrency {
    symbol: string,
    name: string,
    color: string,
    data: number[][]
}

interface IProps {
    defaultSymbol?: string
}


const GjaldmidlarPage: React.FC<IProps> = ({
    defaultSymbol
}) => {
    const accessToken = useContext(AccessTokenContext);
    const [ defaultProps, setDefaultProps ] = useState<IDefaultProps>({
        accessToken,
        refreshRateMs: 60*1000
    });

    useEffect(() => {
        setDefaultProps({
            ...defaultProps,
            accessToken
        });
        
        
    }, [accessToken]);

    const queries = [
        {
            columns: 2,
            query: 'min-width: 1024px'
        }, {
            columns: 3,
            query: 'min-width: 1280px'
        }
    ];

    const currencySymbols: CurrenciesChartType[] = ['KIB', 'USD', 'EUR', 'GBP', 'CAD', 'JPY', 'CHF', 'DKK', 'SEK', 'NOK', 'PLN', 'XDR'];

    const queriesForUpper = [
         {
            columns: 2,
            query: 'min-width: 1024px'
        }
    ];
    // List of funds that are visible in the chart.
    const [visibleChartCurrencies, setVisibleChartCurrencies] = useState<IVisibleCurrency[]>([{
        symbol: defaultSymbol ? defaultSymbol : 'CBIISK',
        name: defaultSymbol ? defaultSymbol : 'CBIISK',
        color: '#dc1e35',
        data: []
    }]);
    const today = new Date();
    const from = new Date(today.getFullYear() - 5, today.getMonth() + 1, today.getDate());
    // Hook used for fetching chart data for clicked symbol.
    const [symbolToFetchFor, setSymbolToFetchFor, newDataForFetchedSymbol] = useApiLmdDataMappedByString<IApiLmdCurrency[]>(
        visibleChartCurrencies[0]?.symbol ?? '',
        (symbol: string) => `/v1/market_data/v1/currency_cross/base_currency/${symbol.substring(0, 3).toUpperCase()}/cross_currency/${symbol.substring(3, 6).toUpperCase()}/history/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`,
        accessToken,
        MARKET_REFRESH_RATES['CURRENCYCHART']
    );

    const handleCurrencyClick = (symbol: string | null) => {
        if (symbol === null) return;
        if (newDataForFetchedSymbol.data === null && newDataForFetchedSymbol.error === null) return;
        const existingChartCurrencies = visibleChartCurrencies.find(chartCurrency => chartCurrency.symbol === symbol);
        if (existingChartCurrencies === undefined) {
            if (symbol === symbolToFetchFor) {
                setVisibleChartCurrencies(visibleChartCurrencies.concat([{
                    symbol,
                    name: symbol,
                    data: newDataForFetchedSymbol.data?.map((item) => [Date.parse(item.Datetimerate ?? ''), item.Averagerate ?? 0]) ?? [],
                    color: `#${((Math.floor(Math.random() * 16777215)).toString(16)).padStart(6, '0')}`
                }]))
            } else {
                setVisibleChartCurrencies(visibleChartCurrencies.concat([{
                    symbol,
                    name: 'Hleður...',
                    color: `#${((Math.floor(Math.random() * 16777215)).toString(16)).padStart(6, '0')}`,
                    data: []
                }]));
                setSymbolToFetchFor(symbol);
            }
        } else {
            setVisibleChartCurrencies(visibleChartCurrencies.filter(chartCurrency => chartCurrency.symbol !== symbol));
        }
    }
    useEffect(() => {
        const existingVisibleChartCurrenciesIndex = visibleChartCurrencies.findIndex(visibleChartCurrencies => visibleChartCurrencies.symbol === symbolToFetchFor);
        if (existingVisibleChartCurrenciesIndex !== -1 && newDataForFetchedSymbol.data !== null) {
            const existingVisibleChartCurrencies = visibleChartCurrencies[existingVisibleChartCurrenciesIndex];
            const newVisibleChartCurrenciess = [...visibleChartCurrencies];
            newVisibleChartCurrenciess.splice(existingVisibleChartCurrenciesIndex, 1, {
                ...existingVisibleChartCurrencies,
                name: symbolToFetchFor,
                data: newDataForFetchedSymbol.data.map((item) => [Date.parse(item.Datetimerate ?? ''), item.Averagerate ?? 0])
            })
            setVisibleChartCurrencies(newVisibleChartCurrenciess);
        }
    }, [symbolToFetchFor, newDataForFetchedSymbol]);
    return (
        <div className='main KCL_gjaldmidlar-page'>
            <div className="main__inner-fluid">
                <div className="shell">
                    <HeadSection 
                        hasAds='market'
                        title='Gjaldmiðlar'
                    />
                    <div className='section'>
                        <div className='main__cols grid-items' >
                            <Columns className="custom-cols" queries={queriesForUpper}>
                                <div className="grid-item">
                                    <CurrenciesSingleBase
                                        accessToken={accessToken}
                                        refreshRateMs={MARKET_REFRESH_RATES['CURRENCIESSINGLEBASE']}
                                        id="CurrenciesPage_CurrenciesSingleBase"
                                        onSymbolClick={handleCurrencyClick}
                                    />
                                </div>
                                <div className="grid-item" id="sogulegt-gengi">
                                    <GjaldmidlarChart
                                        title="Sögulegt gengi"
                                        accessToken={accessToken}
                                        refreshRateMs={MARKET_REFRESH_RATES['CURRENCYCHART']}
                                        id="CurrenciesPage_HistoryChart"
                                        data={visibleChartCurrencies}
                                    />
                                </div>
                            </Columns>
                        </div>
                        {window.matchMedia('(max-width: 768px)').matches 
                        ? <AdRotator location="Market400x80" />
                        : <AdRotator location="Market1018x360" />
                        }
                        <div className='section-before-charts'>
                            <h2 id="Currencies_IntradayRates">Almennt gengi innan dags</h2>
                            <a className="label-wrap" href="https://islandsbanki.is" target="_blank" rel="noopener noreferrer">
                                <Label text="Íslandsbanki" labelType="origin" />
                            </a>
                        </div>
                        <div className='main__cols grid-items'>
                            <Columns className="custom-cols" queries={queries}>
                                {currencySymbols.map((symbol, index) => (
                                    <div key={index} className="grid-item" >
                                        <CurrencyIntradayChart
                                            currencySymbol={symbol}
                                            accessToken={accessToken}
                                            refreshRateMs={MARKET_REFRESH_RATES['INTRADAYCHART']}
                                            id={`CurrenciesPage_IntradayChart${symbol}`}
                                        />
                                    </div>
                                ))}
                            </Columns>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GjaldmidlarPage;
